/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useContext } from 'react';

import { css, jsx } from '@compiled/react';
import { type EgressFilteringService } from '@forge/egress';

import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { VisitorCollection } from '@atlaskit/adf-utils/types';
import type {
	ExtensionHandler,
	ExtensionHandlers,
	ExtensionParams,
	Parameters as ExtensionParameters,
} from '@atlaskit/editor-common/extensions';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ProviderName, Providers } from '@atlaskit/editor-common/provider-factory';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { useMediaClient } from '@atlaskit/media-client-react/use-media-client';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ReactRenderer, type RendererProps } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import {
	type BridgeConfig,
	EgressErrorMessage,
	EmbeddedMacroRenderer,
	type GetContextTokenFn,
	isValidImageUrl,
	RendererContext,
	sanitizeUrl,
	useEgressPermissionService,
	useNavigation,
} from '@atlassian/forge-ui';
import type { RenderFn } from '@atlassian/forge-ui-types/components';
import type { ContextId, ForgeUIExtensionType } from '@atlassian/forge-ui-types/extension';
import { ForgeUIBodiedMacroEmbeddedContentAnalytics } from '@atlassian/forge-ui/analytics/bodied-macro-embedded-content-analytics';
import type { IframeProps } from '@atlassian/forge-ui/custom-ui/iframe/types';

import { getEmojiProvider } from './emoji-provider';
import {
	createForgeParametersForConnectMacro,
	getMigratedForgeMacroExtension,
	type MacroCoreParameters,
} from './migrated-connect-macro';
import {
	type AdfNode,
	getNestedExtensionTableAnalysis,
	getNestedExtensionTableWidth,
	NESTED_EXTENSION_TABLE_INLINE_START_OFFSET,
	resizeNestedExtensionTables,
	useForgeExtensionContainerWidth,
	useNestedExtensionTableWidth,
} from './nested-extension-table-width';

type EmbeddedMacroContext = {
	accountId: string;
	cloudId: string;
	contextIds: string[];
	extensionData: Record<string, unknown>;
};

type ForgeMacroParametersWithAnalytics = ExtensionParameters & {
	isMigrated?: boolean;
};

const EMPTY_EXTENSION_VIEW_DATA = Object.freeze({});
interface Dependencies {
	bridge?: BridgeConfig;
	/** The cloud ID of the current site, used to construct the site-specific custom emoji provider URL */
	cloudId?: string;
	/** v2 context-aware custom bridge methods, gated on forge-ui-bridge-core-reset */
	customBridge?: IframeProps['customBridge'];
	/** Confluence custom bridge methods to be injected for embedded macros */
	customBridgeMethods?: Record<string, (...args: any[]) => any>;
	/**
	 * Host ProviderFactory forwarded from the surrounding page. makeADFRenderer copies the providers
	 * the nested renderer cares about off this (e.g. the task-decision and context-identifier
	 * providers, so action items inside a bodied macro body persist their checked state) and merges
	 * them with the custom emoji provider into the factory handed to the nested renderer.
	 */
	dataProviders?: ProviderFactory;
	extensionData?: {
		[key: string]: unknown;
		content?: { id?: string; subtype?: string | null; type?: string };
		references?: unknown[];
		space?: { id?: string; key?: string };
		type?: string;
	};
	/** Installed Forge macro extensions used to identify migrated Connect macros */
	forgeMacroExtensions?: ReadonlyArray<ForgeUIExtensionType> | null;
	getContextToken?: GetContextTokenFn;
	isEditing?: boolean;
	/** Product context IDs used to render nested migrated Forge macros */
	productContextIds?: ContextId[];
	productExtensionHandlers?: ExtensionHandlers;
	/** The account ID of the current user, used to set user-specific emoji preferences (e.g. skin tone) */
	userId?: string | null;
}

// Keep in sync with platform/packages/forge/forge-react-types/src/components/adfrenderer/index.ts
export type AdfRendererProps = {
	/**
	 * An ADF document to render
	 *
	 * @type [DocNode](https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/doc/)
	 */
	// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
	document: DocNode;

	// documentWithoutMedia is only used internally on the Forge side, this is not part of the public API
	documentWithoutMedia?: DocNode;

	/**
	 * A function to determine behaviour for handling unsupported nodes:
	 * - Return a new Node to replace the unsupported one
	 * - Return false to remove the node entirely
	 * - Return undefined to leave the node as-is (default behaviour)
	 *
	 * @type [Visitor](https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/master/editor/adf-utils/src/types/index.ts)
	 */
	replaceUnsupportedNode?: Function;
};

type AdfRendererInternalProps = AdfRendererProps & {
	eventHandlers: RendererProps['eventHandlers'];
};

const baseRendererFontStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: 'var(--ak-renderer-base-font-size)',
});

const makeProductMacroCoreHandlerWithBaseFontStyles = (
	productHandlerEntry: ExtensionHandlers[string],
): ExtensionHandlers[string] => {
	const wrapProductHandler = <T extends ExtensionParameters>(
		productHandler: ExtensionHandler<T>,
	): ExtensionHandler<T> => {
		return (extensionProps: ExtensionParams<T>, doc: object) => {
			const result = productHandler(extensionProps, doc);
			if (result == null) {
				return result;
			}
			return (
				<div
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ '--ak-renderer-base-font-size': '16px' } as React.CSSProperties}
					css={baseRendererFontStyles}
				>
					{result}
				</div>
			);
		};
	};

	return typeof productHandlerEntry === 'function'
		? wrapProductHandler(productHandlerEntry)
		: { ...productHandlerEntry, render: wrapProductHandler(productHandlerEntry.render) };
};

const filterInvalidMediaUrls = (
	appDomainName: string | undefined,
	egressServices: EgressFilteringService[],
	adfDocument: DocNode,
): {
	filteredDocument: DocNode;
	invalidUrls: string[];
} => {
	const invalidUrls = new Set<string>();

	const visitors: VisitorCollection = {
		media: (node) => {
			const url = node?.attrs?.url;
			if (url && !egressServices.some((service) => isValidImageUrl(url, service, appDomainName))) {
				invalidUrls.add(url);
				return {
					...node,
					attrs: {
						...node.attrs,
						// Remove the URL so it fails to load
						// We use a space because an empty string causes an infinite loading spinner
						url: ' ',
					},
				};
			}
		},
	};

	const filteredDocument = traverse(adfDocument, visitors);

	return {
		filteredDocument: filteredDocument ? (filteredDocument as DocNode) : adfDocument,
		invalidUrls: Array.from(invalidUrls),
	};
};

const useSupportsMedia = () => {
	try {
		useMediaClient();
		return true;
		// eslint-disable-next-line no-unused-vars
	} catch (e) {
		// Throws if no MediaClientProvider is found
		return false;
	}
};

// Module-level cache keyed on `cloudId:userId` — created once per page load, reused across all extensions
const emojiProviderCache = new Map<string, Promise<EmojiProvider>>();

export const getCachedEmojiProvider = (
	cloudId: string | undefined,
	userId: string | null | undefined,
): Promise<EmojiProvider> | undefined => {
	if (!fg('platform_forge_ui_custom_emoji_ui_kit_adf_renderer') || !cloudId) {
		return undefined;
	}
	const cacheKey = `${cloudId}:${userId ?? ''}`;
	const cached = emojiProviderCache.get(cacheKey);
	if (cached) {
		return cached;
	}
	const emojiProvider = getEmojiProvider(cloudId, userId ?? null);
	emojiProviderCache.set(cacheKey, emojiProvider);
	return emojiProvider;
};

// Reads the current value of a provider off a ProviderFactory without leaving a lingering
// subscription. Returns `undefined` when the factory or provider is absent.
const getProviderFromFactory = <T extends ProviderName>(
	factory: ProviderFactory | undefined,
	name: T,
) => {
	if (!factory) {
		return undefined;
	}
	const noop = () => {};
	const provider = factory.subscribe(name, noop);
	factory.unsubscribe(name, noop);
	return provider;
};

// Reads a single provider off the first supplied source factory that exposes it, returning a
// spreadable `{ [name]: provider }` fragment (or `undefined` when no source has it). When several
// sources expose the same provider, earlier sources win.
const pickProvider = <T extends ProviderName>(
	name: T,
	...sources: Array<ProviderFactory | undefined>
): Pick<Providers, T> | undefined => {
	const source = sources.find((factory) => factory?.hasProvider(name));
	return source
		? ({ [name]: getProviderFromFactory(source, name) } as Pick<Providers, T>)
		: undefined;
};

// Builds the single ProviderFactory handed to the nested renderer: the custom emoji provider passed
// in directly, plus the task-decision and context-identifier providers copied off the host factory
// (never mutating it) so action items inside a bodied macro body can persist their checked state.
const mergeNestedDataProviders = (
	hostDataProviders: ProviderFactory | undefined,
	emojiProvider: Promise<EmojiProvider> | undefined,
): ProviderFactory =>
	ProviderFactory.create({
		...(emojiProvider ? { emojiProvider } : {}),
		// Action items (checklist toggle) need BOTH providers to persist their checked state:
		// - taskDecisionProvider performs the actual write (toggleTask -> backend mutation).
		// - contextIdentifierProvider supplies the objectId that ResourcedTaskItem resolves into the
		//   objectAri key for that write. Without it the provider has no target to persist against, so
		//   ResourcedTaskItem falls back to local-only state and the toggle is lost on refresh.
		...pickProvider('taskDecisionProvider', hostDataProviders),
		...pickProvider('contextIdentifierProvider', hostDataProviders),
	});

export const makeADFRenderer = ({
	bridge,
	cloudId,
	customBridgeMethods,
	customBridge,
	getContextToken,
	extensionData,
	forgeMacroExtensions,
	isEditing,
	productContextIds,
	productExtensionHandlers,
	dataProviders: hostDataProviders,
	userId,
}: Dependencies) => {
	const dataProviderFactory = mergeNestedDataProviders(
		hostDataProviders,
		getCachedEmojiProvider(cloudId, userId),
	);

	return (props: Parameters<RenderFn>[0]): React.JSX.Element => (
		<AdfRenderer
			bridge={bridge}
			cloudId={cloudId}
			dataProviders={dataProviderFactory}
			getContextToken={getContextToken}
			extensionData={extensionData}
			forgeMacroExtensions={forgeMacroExtensions}
			customBridgeMethods={customBridgeMethods}
			customBridge={customBridge}
			isEditing={isEditing}
			productContextIds={productContextIds}
			productExtensionHandlers={productExtensionHandlers}
			userId={userId}
			{...props}
		/>
	);
};

/**
 * The AdfRenderer component provides a way to render a valid ADF document, using the same renderer that Atlassian uses internally to render ADF content in Confluence pages, Jira work items, and so on.
 * It allows you to replace node types that are unsupported in the context of a Forge app with replacement content, or remove them entirely.
 * See [Atlassian Document Format](/cloud/jira/platform/apis/document/structure/) for information on valid nodes.
 *
 * This component uses [@atlaskit/renderer](https://www.npmjs.com/package/@atlaskit/renderer) under the hood.
 *
 * Visit [Renderer editor](https://atlaskit.atlassian.com/examples/editor/renderer/basic) for a comprehensive list of different ADF document examples
 */
const makeExtensionHandlers = ({
	bridge,
	cloudId,
	embeddedMacroCustomBridgeMethods,
	customBridge,
	extensionData,
	forgeMacroExtensions,
	isEditing,
	getContextToken,
	productContextIds,
	productExtensionHandlers = {},
	userId,
}: {
	bridge: Dependencies['bridge'];
	cloudId: Dependencies['cloudId'];
	customBridge: Dependencies['customBridge'];
	embeddedMacroCustomBridgeMethods: Dependencies['customBridgeMethods'];
	extensionData: Dependencies['extensionData'];
	forgeMacroExtensions: Dependencies['forgeMacroExtensions'];
	getContextToken: Dependencies['getContextToken'];
	isEditing: Dependencies['isEditing'];
	productContextIds: Dependencies['productContextIds'];
	productExtensionHandlers: Dependencies['productExtensionHandlers'];
	userId: Dependencies['userId'];
}): ExtensionHandlers | undefined => {
	const forgeMacroHandler: ExtensionHandlers[string] = (props) => {
		const { localId, parameters } = props;
		const isMigrated = (parameters as ForgeMacroParametersWithAnalytics | undefined)?.isMigrated;

		const autoConvertLink = parameters?.autoConvertLink;
		const isInline =
			fg('platform_forge_ui_embedded_inline_macro') && parameters?.layout === 'inlineExtension';

		// Extract config from extension parameters (can be in guestParams or config)
		const config = parameters.guestParams || parameters.config;
		const extensionPayload = {
			config,
			isEditing,
		};

		const embeddedMacroContext = parameters.embeddedMacroContext as EmbeddedMacroContext;
		const extensionId = parameters.extensionId;

		if (!embeddedMacroContext || !extensionId) {
			return null;
		}

		const { accountId, cloudId, contextIds, extensionData } = embeddedMacroContext;

		const augmentedExtensionData = {
			...extensionData,
			autoConvertLink,
		};

		return (
			<EmbeddedMacroRenderer
				accountId={accountId}
				cloudId={cloudId}
				contextIds={contextIds}
				extensionId={extensionId}
				extensionData={augmentedExtensionData}
				extensionPayload={extensionPayload}
				localId={localId}
				product="confluence"
				extensionViewData={EMPTY_EXTENSION_VIEW_DATA}
				bridge={bridge}
				customBridgeMethods={embeddedMacroCustomBridgeMethods}
				customBridge={customBridge}
				getContextToken={getContextToken}
				isInline={isInline}
				isMigrated={isMigrated}
				embeddedMacroParentType={
					fg('platform_forge_ui_refactor_macro_parent_type') ? 'UIKit' : undefined
				}
			/>
		);
	};

	const baseExtensionHandlers: ExtensionHandlers = {
		'com.atlassian.ecosystem': forgeMacroHandler,
	};

	const productMacroCoreHandlerEntry =
		productExtensionHandlers['com.atlassian.confluence.macro.core'];
	const productMacroCoreHandler =
		productMacroCoreHandlerEntry && fg('platform_forge_nested_macro_font_fix')
			? makeProductMacroCoreHandlerWithBaseFontStyles(productMacroCoreHandlerEntry)
			: productMacroCoreHandlerEntry;
	const renderProductMacroCoreHandler =
		typeof productMacroCoreHandler === 'function'
			? productMacroCoreHandler
			: productMacroCoreHandler?.render;
	const renderProductMacroCore: ExtensionHandlers[string] = (props, doc) =>
		renderProductMacroCoreHandler?.(props, doc) ?? null;
	const renderProductMacroCoreWithAnalytics: ExtensionHandlers[string] = (props, doc) => {
		const result = renderProductMacroCore(props, doc);
		if (!result) {
			return result;
		}
		const isInline = props.type === 'inlineExtension';

		return (
			<ForgeUIBodiedMacroEmbeddedContentAnalytics
				contentId={extensionData?.content?.id}
				embeddedMacroParentType="UIKit"
				embeddedMacroType="productMacroCore"
				extensionKey={props.extensionKey}
				isInline={isInline}
			>
				{result}
			</ForgeUIBodiedMacroEmbeddedContentAnalytics>
		);
	};
	const shouldTrackNestingAnalytics = FeatureGates.getExperimentValue(
		'platform_forge_nesting_analytics',
		'isEnabled',
		false,
	);
	const renderProductCoreHandlerOrForgeMacroHandler: ExtensionHandlers[string] = (props, doc) => {
		if (forgeMacroExtensions !== undefined) {
			if (forgeMacroExtensions === null) {
				return null;
			}

			const migratedForgeExtension = getMigratedForgeMacroExtension(
				forgeMacroExtensions,
				props.extensionKey,
			);

			if (migratedForgeExtension) {
				const parameters = createForgeParametersForConnectMacro({
					accountId: userId,
					cloudId,
					extension: migratedForgeExtension,
					extensionData,
					productContextIds,
					props: props as ExtensionParams<MacroCoreParameters>,
				});

				if (!parameters) {
					return null;
				}

				const result = forgeMacroHandler(
					{
						...props,
						parameters: {
							...parameters,
							isMigrated: shouldTrackNestingAnalytics ? true : undefined,
						},
					},
					doc,
				);

				return result;
			}

			if (!productMacroCoreHandler) {
				return null;
			}

			return shouldTrackNestingAnalytics
				? renderProductMacroCoreWithAnalytics(props, doc)
				: renderProductMacroCore(props, doc);
		}

		if (!productMacroCoreHandler) {
			return null;
		}

		return shouldTrackNestingAnalytics
			? renderProductMacroCoreWithAnalytics(props, doc)
			: renderProductMacroCore(props, doc);
	};
	const productMigrationHandler = productExtensionHandlers?.['com.atlassian.confluence.migration'];
	const renderProductMigrationHandler =
		typeof productMigrationHandler === 'function'
			? productMigrationHandler
			: productMigrationHandler?.render;
	const renderProductMigrationWithAnalytics: ExtensionHandlers[string] = (props, doc) => {
		const result = renderProductMigrationHandler?.(props, doc) ?? null;
		if (!result) {
			return result;
		}

		const isInline = props.type === 'inlineExtension';
		return (
			<ForgeUIBodiedMacroEmbeddedContentAnalytics
				contentId={extensionData?.content?.id}
				embeddedMacroParentType="UIKit"
				embeddedMacroType="productMigration"
				extensionKey={props.extensionKey}
				isInline={isInline}
			>
				{result}
			</ForgeUIBodiedMacroEmbeddedContentAnalytics>
		);
	};
	const productMigrationHandlerWithAnalytics =
		productMigrationHandler && shouldTrackNestingAnalytics
			? renderProductMigrationWithAnalytics
			: productMigrationHandler;

	if (
		!FeatureGates.getExperimentValue(
			'platform_forge_uikit_render_migrated_macro',
			'isEnabled',
			false,
		)
	) {
		return {
			...baseExtensionHandlers,
			...(productMacroCoreHandler && {
				'com.atlassian.confluence.macro.core': productMacroCoreHandler,
			}),
			...(productMigrationHandlerWithAnalytics && {
				'com.atlassian.confluence.migration': productMigrationHandlerWithAnalytics,
			}),
		};
	}

	return {
		...baseExtensionHandlers,
		'com.atlassian.confluence.macro.core': renderProductCoreHandlerOrForgeMacroHandler,
		...(productMigrationHandlerWithAnalytics && {
			'com.atlassian.confluence.migration': productMigrationHandlerWithAnalytics,
		}),
	};
};

export const AdfRenderer = (props: Parameters<RenderFn>[0] & Dependencies): React.JSX.Element => {
	const supportsMedia = useSupportsMedia();
	const { egress, properties, appDomainName, forgeEnvironment } = useContext(RendererContext);
	const imagesEgressService = useEgressPermissionService('IMAGES', egress);
	const mediaEgressService = useEgressPermissionService('MEDIA', egress);

	const {
		bridge,
		cloudId,
		customBridgeMethods: embeddedMacroCustomBridgeMethods,
		customBridge,
		dataProviders,
		extensionData,
		forgeMacroExtensions,
		isEditing,
		getContextToken,
		productContextIds,
		productExtensionHandlers,
		userId,
	} = props;

	const { onNavigate, getModalJsx } = useNavigation({ extension: { egress, properties } });

	const {
		document: adfDocument,
		documentWithoutMedia,
		...adfRendererProps
	} = props.forgeDoc.props as AdfRendererInternalProps;
	const isNestedExtensionTableWidthFixEnabled = fg('platform_forge_nested_table_width_fix');

	// The client provides both the document with media and without media, as it doesn't know whether the MediaClientProvider is present
	// Then, in the parent product, we can decide whether to render the document with media or without media
	const doc = !supportsMedia && documentWithoutMedia ? documentWithoutMedia : adfDocument;
	const { nestedExtensionTableMinWidth } = React.useMemo(
		() =>
			isNestedExtensionTableWidthFixEnabled
				? getNestedExtensionTableAnalysis(doc)
				: { containsExtension: false, nestedExtensionTableMinWidth: undefined },
		[doc, isNestedExtensionTableWidthFixEnabled],
	);
	const { extensionContainerWidth, rendererContainerRef } = useForgeExtensionContainerWidth(
		nestedExtensionTableMinWidth !== undefined,
	);
	const nestedExtensionTableWidth = getNestedExtensionTableWidth(
		nestedExtensionTableMinWidth,
		extensionContainerWidth,
	);
	const nestedExtensionRendererMinWidth = nestedExtensionTableWidth
		? nestedExtensionTableWidth + NESTED_EXTENSION_TABLE_INLINE_START_OFFSET
		: undefined;
	const rendererWidth = Math.max(
		extensionContainerWidth ?? 0,
		nestedExtensionRendererMinWidth ?? 0,
	);
	useNestedExtensionTableWidth(rendererContainerRef, nestedExtensionTableWidth);
	const rendererContainerStyle = rendererWidth
		? ({ width: `${rendererWidth}px` } as React.CSSProperties)
		: undefined;
	const { filteredDocument, invalidUrls } = React.useMemo(
		() =>
			doc
				? filterInvalidMediaUrls(appDomainName, [imagesEgressService, mediaEgressService], doc)
				: { filteredDocument: undefined, invalidUrls: [] },
		[appDomainName, doc, imagesEgressService, mediaEgressService],
	);
	const rendererDocument = React.useMemo(
		() =>
			filteredDocument && nestedExtensionTableWidth
				? (resizeNestedExtensionTables(
						filteredDocument as AdfNode,
						nestedExtensionTableWidth,
					) as DocNode)
				: filteredDocument,
		[filteredDocument, nestedExtensionTableWidth],
	);

	if (!doc || !rendererDocument) {
		return <></>;
	}
	const renderer = (
		<RendererActionsContext>
			<ReactRenderer
				{...adfRendererProps}
				{...(isNestedExtensionTableWidthFixEnabled && {
					appearance: 'full-width' as const,
					UNSTABLE_allowTableAlignment: true,
					UNSTABLE_allowTableResizing: true,
					allowFixedColumnWidthOption: true,
				})}
				allowCustomPanels={fg('confluence_fix_migrated_macro_inline_rendering')}
				dataProviders={dataProviders}
				eventHandlers={{
					link: {
						onClick: (e) => {
							e.preventDefault();
							adfRendererProps?.eventHandlers?.link?.onClick?.(e, e.currentTarget.href);
							onNavigate({ type: 'same-tab', url: sanitizeUrl(e.currentTarget.href) });
						},
					},
				}}
				document={rendererDocument}
				extensionHandlers={makeExtensionHandlers({
					bridge,
					cloudId,
					embeddedMacroCustomBridgeMethods,
					customBridge,
					extensionData,
					forgeMacroExtensions,
					isEditing,
					getContextToken,
					productContextIds,
					productExtensionHandlers,
					userId,
				})}
			/>
		</RendererActionsContext>
	);

	return (
		<>
			{forgeEnvironment === 'DEVELOPMENT' &&
				invalidUrls.map((url) => <EgressErrorMessage key={url} url={url} />)}
			{getModalJsx()}
			{isNestedExtensionTableWidthFixEnabled ? (
				<div
					ref={rendererContainerRef}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={rendererContainerStyle}
				>
					{renderer}
				</div>
			) : (
				renderer
			)}
		</>
	);
};
